import { PATHS } from 'src/common/constants';
import { useBranding } from 'src/branding/BrandingContextProvider';
import {
  ServiceRequestFormContextValue,
  useServiceRequestFormContext,
} from 'src/components/ServiceRequestForm/CustomHooks/ServiceRequestFormContext';

export interface UseServiceRequestFormReviewOrchestratorReturn {
  confirmationHeader: string;
  confirmationBody: string;
  theme: string;
  ticketNumber: string;
  issueType: string;
  assetName: string;
  make: string;
  model: string;
  suiteAndProperty: string;
  buildingName: string;
  unitAddress: string;
  isStaffReviewer: boolean;
  serviceRequestViewPath: string;
  onViewServiceRequest: () => void;
  onViewServiceRequestLink: () => void;
  onExit: () => void;
}

const STAFF_REVIEWER_ROLES: string[] = [
  'Admin',
  'Super Admin',
  'Property Developer',
];

export const useServiceRequestFormReviewOrchestrator =
  (): UseServiceRequestFormReviewOrchestratorReturn => {
    const {
      formData,
      user,
      onExit,
      reference,
    }: ServiceRequestFormContextValue = useServiceRequestFormContext();

    const { properties, propertySuites, getBuildingName } = reference;

    const { branding } = useBranding();

    const selectedProperty: any =
      properties.find((property: any) => property.id === formData.property_id) ??
      null;

    const selectedSuite: any =
      propertySuites.find((suite: any) => suite.id === formData.suite_id) ?? null;

    const buildingName: string = getBuildingName(selectedSuite?.buildingId);

    const unitAddress: string = selectedSuite?.warrantyAddress ?? '';

    const theme: string = localStorage.getItem('theme') ?? '';

    const ticketNumber: string =
      localStorage.getItem('ticketNumber') ||
      localStorage.getItem('ticketId') ||
      '';

    const serviceRequestId: string =
      localStorage.getItem('serviceRequestId') ?? '';

    const userRole: string = user?.roles?.[0]?.role?.name;

    const isStaffReviewer: boolean = STAFF_REVIEWER_ROLES.includes(userRole);

    const clearTicketStorage = (): void => {
      localStorage.removeItem('ticketId');
      localStorage.removeItem('serviceRequestId');
    };

    const onViewServiceRequest = (): void => {
      try {
        window.history.replaceState(
          {},
          '',
          '/app/service-requests/' + serviceRequestId
        );

        clearTicketStorage();

        window.location.reload();
      } catch (error) {
        console.error(
          `Unexpected error encountered in the Service Request Form Review: ${error}`
        );
      }
    };

    const onViewServiceRequestLink = (): void => {
      clearTicketStorage();
      onExit();
    };

    return {
      confirmationHeader: branding.text.serviceRequestConfirmationHeader,
      confirmationBody: branding.text.serviceRequestConfirmationBody,
      theme,
      ticketNumber,
      issueType: formData.issueType,
      assetName: formData.asset_item_nameDescription,
      make: formData.asset_item_make ? formData.asset_item_make : '—',
      model: formData.asset_item_model ? formData.asset_item_model : '—',
      suiteAndProperty:
        (selectedSuite?.number ?? '') + ' / ' + (selectedProperty?.name ?? ''),
      buildingName,
      unitAddress,
      isStaffReviewer,
      serviceRequestViewPath: PATHS.SERVICE_REQUESTS_VIEW.replace(
        ':id',
        serviceRequestId
      ),
      onViewServiceRequest,
      onViewServiceRequestLink,
      onExit,
    };
  };